import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocalStorage } from '../../hooks/local-storage';

interface DrawerContextType {
  isDrawerOpen: boolean;
  drawerWidth: number;
  isResizing: boolean;
  setIsResizing: (resizing: boolean) => void;
  setDrawerWidth: (width: number) => void;
  toggleDrawer: () => void;
  openDrawer: () => void;
  closeDrawer: () => void;
}

const DrawerContext = createContext<DrawerContextType | undefined>(undefined);

export function DrawerProvider({ children }: { children: React.ReactNode }) {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [storedDrawerWidth, setStoredDrawerWidth] = useLocalStorage<number>(
    'drawer-width',
    320
  );
  const [drawerWidth, setDrawerWidthState] =
    useState<number>(storedDrawerWidth);
  const [isResizing, setIsResizing] = useState<boolean>(false);

  const toggleDrawer = () => setIsDrawerOpen((prev) => !prev);
  const openDrawer = () => setIsDrawerOpen(true);
  const closeDrawer = () => setIsDrawerOpen(false);

  const setDrawerWidth = (width: number) => {
    setDrawerWidthState(width);
    setStoredDrawerWidth(width);
  };

  // Sync with localStorage when drawerWidth changes externally
  useEffect(() => {
    setDrawerWidthState(storedDrawerWidth);
  }, [storedDrawerWidth]);

  return (
    <DrawerContext.Provider
      value={{
        isDrawerOpen,
        drawerWidth,
        isResizing,
        setIsResizing,
        setDrawerWidth,
        toggleDrawer,
        openDrawer,
        closeDrawer,
      }}
    >
      {children}
    </DrawerContext.Provider>
  );
}

export function useDrawer() {
  const context = useContext(DrawerContext);
  if (context === undefined) {
    throw new Error('useDrawer must be used within a DrawerProvider');
  }
  return context;
}
